import { useEffect, useState } from 'react';
import axios from 'axios';
import { DashboardLayout } from '../../components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { formatDate, formatApiError } from '../../lib/utils';
import { Plus, Copy, Check, Ticket, Calendar, QrCode, Mail, Phone, Download, Send, Loader2, X, FileText } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

export default function OperatorCodes() {
    const [codes, setCodes] = useState([]);
    const [attractions, setAttractions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [sendDialogOpen, setSendDialogOpen] = useState(false);
    const [qrDialogOpen, setQrDialogOpen] = useState(false);
    const [selectedCode, setSelectedCode] = useState(null);
    const [copiedCode, setCopiedCode] = useState(null);
    const [sending, setSending] = useState(false);
    const [filter, setFilter] = useState('all');
    
    const [formData, setFormData] = useState({
        attraction_ids: [],
        quantity: 1,
        expiration_days: 365,
        max_uses: 2,
        generate_pdf: false
    });
    
    const [sendData, setSendData] = useState({
        method: 'email',
        email: '',
        phone: '',
        traveler_name: ''
    });

    useEffect(() => {
        Promise.all([fetchCodes(), fetchAttractions()]);
    }, []);

    const fetchCodes = async () => {
        try {
            const response = await axios.get(`${API}/api/operator/codes`, { withCredentials: true });
            setCodes(response.data);
        } catch (error) {
            console.error('Failed to fetch codes:', error);
        } finally {
            setLoading(false);
        }
    };

    const fetchAttractions = async () => {
        try {
            const response = await axios.get(`${API}/api/operator/attractions`, { withCredentials: true });
            const allAttractions = response.data.map(a => ({
                ...a,
                city_name: a.city_name || ''
            }));
            setAttractions(allAttractions);
        } catch (error) {
            console.error('Failed to fetch attractions:', error);
        }
    };

    const handleGenerate = async (e) => {
        e.preventDefault();
        if (formData.attraction_ids.length === 0) {
            toast.error('Please select at least one attraction');
            return;
        }
        try {
            const response = await axios.post(`${API}/api/operator/codes/generate`, formData, { withCredentials: true });
            const newCodes = response.data.codes;
            toast.success(`Generated ${newCodes.length} codes! Used ${response.data.credits_used} credits.`);
            setDialogOpen(false);
            fetchCodes();

            // If generate_pdf was checked, auto-download PDF
            if (formData.generate_pdf && newCodes.length > 0) {
                downloadPdf(newCodes.map(c => c.id));
            }
        } catch (error) {
            toast.error(formatApiError(error.response?.data?.detail));
        }
    };

    const downloadPdf = async (codeIds) => {
        try {
            toast.info('Generating PDF...');
            const response = await axios.post(`${API}/api/operator/codes/generate-pdf`, { code_ids: codeIds }, {
                withCredentials: true,
                responseType: 'blob'
            });
            const blob = new Blob([response.data], { type: 'application/pdf' });
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `vouchers_${new Date().toISOString().slice(0,10)}.pdf`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(url);
            toast.success('PDF downloaded!');
        } catch (error) {
            toast.error('Failed to generate PDF');
        }
    };

    const handleSendCode = async (e) => {
        e.preventDefault();
        if (!selectedCode) return;
        
        setSending(true);
        try {
            if (sendData.method === 'email') {
                await axios.post(
                    `${API}/api/operator/codes/${selectedCode.id}/send-email?email=${encodeURIComponent(sendData.email)}&traveler_name=${encodeURIComponent(sendData.traveler_name)}`,
                    {},
                    { withCredentials: true }
                );
                toast.success('Code sent via email!');
            } else {
                await axios.post(
                    `${API}/api/operator/codes/${selectedCode.id}/send-sms?phone=${encodeURIComponent(sendData.phone)}&traveler_name=${encodeURIComponent(sendData.traveler_name)}`,
                    {},
                    { withCredentials: true }
                );
                toast.success('Code sent via SMS!');
            }
            setSendDialogOpen(false);
            setSendData({ method: 'email', email: '', phone: '', traveler_name: '' });
        } catch (error) {
            toast.error(formatApiError(error.response?.data?.detail));
        } finally {
            setSending(false);
        }
    };

    const copyCode = (code) => {
        navigator.clipboard.writeText(code);
        setCopiedCode(code);
        setTimeout(() => setCopiedCode(null), 2000);
        toast.success('Code copied!');
    };

    const openSendDialog = (code) => {
        setSelectedCode(code);
        setSendDialogOpen(true);
    };

    const openQrDialog = (code) => {
        setSelectedCode(code);
        setQrDialogOpen(true);
    };

    const downloadQr = () => {
        if (!selectedCode) return;
        const link = document.createElement('a');
        link.href = `${API}/api/qr/${selectedCode.code}`;
        link.download = `qr-${selectedCode.code}.png`;
        link.click();
    };

    const filteredCodes = codes.filter(code => {
        if (filter === 'all') return true;
        if (filter === 'active') return code.is_active && code.current_uses < code.max_uses;
        if (filter === 'used') return code.current_uses >= code.max_uses;
        if (filter === 'expired') return new Date(code.expires_at) < new Date();
        return true;
    });

    const exportToCSV = () => {
        window.open(`${API}/api/operator/codes/export?status=${filter === 'all' ? '' : filter}`, '_blank');
    };

    if (loading) {
        return (
            <DashboardLayout type="operator">
                <div className="flex items-center justify-center h-64">
                    <div className="w-8 h-8 border-2 border-gold border-t-transparent rounded-full animate-spin" />
                </div>
            </DashboardLayout>
        );
    }

    return (
        <DashboardLayout type="operator">
            <div className="space-y-8" data-testid="operator-codes">
                {/* Header */}
                <div className="flex items-center justify-between">
                    <div>
                        <h1 className="font-outfit text-2xl lg:text-3xl font-bold text-white">Redeem Codes</h1>
                        <p className="text-muted-foreground mt-1">Generate and manage redeem codes</p>
                    </div>
                    <div className="flex items-center gap-3">
                        <Button 
                            variant="outline" 
                            onClick={exportToCSV}
                            className="border-white/10 text-muted-foreground hover:text-white gap-2"
                            data-testid="export-csv-btn"
                        >
                            <Download className="w-4 h-4" />
                            Export CSV
                        </Button>
                        <Button
                            variant="outline"
                            onClick={async () => {
                                try {
                                    const res = await axios.get(`${API}/api/operator/codes/export-pdf${filter !== 'all' ? `?status=${filter}` : ''}`, {
                                        withCredentials: true,
                                        responseType: 'blob'
                                    });
                                    const url = URL.createObjectURL(res.data);
                                    const a = document.createElement('a');
                                    a.href = url;
                                    a.download = 'QR_Codes.pdf';
                                    a.click();
                                    URL.revokeObjectURL(url);
                                    toast.success('QR PDF downloaded');
                                } catch (e) {
                                    toast.error('Failed to export QR PDF');
                                }
                            }}
                            className="border-white/10 text-muted-foreground hover:text-white gap-2"
                            data-testid="export-pdf-btn"
                        >
                            <QrCode className="w-4 h-4" />
                            Export QR PDF
                        </Button>
                        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                            <DialogTrigger asChild>
                                <Button className="bg-gold text-[#0A0D14] hover:bg-gold/90 gap-2" data-testid="generate-codes-btn">
                                    <Plus className="w-4 h-4" /> Generate Codes
                                </Button>
                            </DialogTrigger>
                        <DialogContent className="bg-[#121620] border-white/10 max-w-lg">
                            <DialogHeader>
                                <DialogTitle className="text-white font-outfit">Generate Redeem Codes</DialogTitle>
                            </DialogHeader>
                            <form onSubmit={handleGenerate} className="space-y-4 mt-4">
                                <div className="space-y-2">
                                    <Label>Select Attractions</Label>
                                    <div className="space-y-2">
                                        <button
                                            type="button"
                                            onClick={() => {
                                                if (formData.attraction_ids.length === attractions.length) {
                                                    setFormData({ ...formData, attraction_ids: [] });
                                                } else {
                                                    setFormData({ ...formData, attraction_ids: attractions.map(a => a.id) });
                                                }
                                            }}
                                            className={`w-full text-left px-3 py-2 rounded-md border text-sm transition-colors ${
                                                formData.attraction_ids.length === attractions.length
                                                    ? 'bg-gold/10 border-gold/30 text-gold'
                                                    : 'bg-[#0A0D14] border-white/10 text-muted-foreground hover:border-white/20'
                                            }`}
                                            data-testid="select-all-attractions"
                                        >
                                            {formData.attraction_ids.length === attractions.length
                                                ? `All ${attractions.length} attractions selected`
                                                : 'Select All Attractions'}
                                        </button>
                                        <div className="max-h-40 overflow-y-auto space-y-1 rounded-md border border-white/10 bg-[#0A0D14] p-2">
                                            {attractions.map((attr) => (
                                                <label
                                                    key={attr.id}
                                                    className={`flex items-center gap-2 px-2 py-1.5 rounded text-sm cursor-pointer transition-colors ${
                                                        formData.attraction_ids.includes(attr.id)
                                                            ? 'bg-gold/10 text-gold'
                                                            : 'text-muted-foreground hover:bg-white/5'
                                                    }`}
                                                >
                                                    <input
                                                        type="checkbox"
                                                        checked={formData.attraction_ids.includes(attr.id)}
                                                        onChange={(e) => {
                                                            if (e.target.checked) {
                                                                setFormData({ ...formData, attraction_ids: [...formData.attraction_ids, attr.id] });
                                                            } else {
                                                                setFormData({ ...formData, attraction_ids: formData.attraction_ids.filter(id => id !== attr.id) });
                                                            }
                                                        }}
                                                        className="rounded border-white/20 text-gold focus:ring-gold/50"
                                                        data-testid={`attraction-checkbox-${attr.id}`}
                                                    />
                                                    <span>{attr.title}</span>
                                                    <span className="text-xs text-white/30 ml-auto">{attr.city_name}</span>
                                                </label>
                                            ))}
                                        </div>
                                        {formData.attraction_ids.length > 0 && (
                                            <p className="text-xs text-gold">{formData.attraction_ids.length} attraction{formData.attraction_ids.length > 1 ? 's' : ''} selected</p>
                                        )}
                                    </div>
                                </div>
                                <div className="grid grid-cols-3 gap-4">
                                    <div className="space-y-2">
                                        <Label>Quantity</Label>
                                        <Input
                                            type="number"
                                            value={formData.quantity}
                                            onChange={(e) => setFormData({ ...formData, quantity: parseInt(e.target.value) || 1 })}
                                            min={1}
                                            max={100}
                                            className="bg-[#0A0D14] border-white/10"
                                            data-testid="quantity-input"
                                        />
                                    </div>
                                    <div className="space-y-2">
                                        <Label>Valid Days</Label>
                                        <Input
                                            type="number"
                                            value={formData.expiration_days}
                                            onChange={(e) => setFormData({ ...formData, expiration_days: parseInt(e.target.value) || 365 })}
                                            min={1}
                                            className="bg-[#0A0D14] border-white/10"
                                        />
                                    </div>
                                    <div className="space-y-2">
                                        <Label>Max Devices</Label>
                                        <Input
                                            type="number"
                                            value={formData.max_uses}
                                            onChange={(e) => setFormData({ ...formData, max_uses: parseInt(e.target.value) || 2 })}
                                            min={1}
                                            max={10}
                                            className="bg-[#0A0D14] border-white/10"
                                        />
                                    </div>
                                </div>
                                {/* Credit Cost Preview */}
                                {formData.attraction_ids.length > 0 && (
                                    <div className="p-3 rounded-md bg-gold/5 border border-gold/20">
                                        <div className="flex items-center justify-between">
                                            <span className="text-sm text-muted-foreground">Credit Cost</span>
                                            <span className="text-gold font-semibold" data-testid="credit-cost-preview">
                                                {formData.quantity * formData.attraction_ids.length * formData.max_uses} credits
                                            </span>
                                        </div>
                                        <p className="text-xs text-white/30 mt-1">
                                            {formData.quantity} code{formData.quantity > 1 ? 's' : ''} × {formData.attraction_ids.length} attraction{formData.attraction_ids.length > 1 ? 's' : ''} × {formData.max_uses} device{formData.max_uses > 1 ? 's' : ''}
                                        </p>
                                    </div>
                                )}
                                {/* Generate PDF option */}
                                <label className="flex items-center gap-3 p-3 rounded-md bg-[#0A0D14] border border-white/10 cursor-pointer hover:border-white/20 transition-colors">
                                    <input
                                        type="checkbox"
                                        checked={formData.generate_pdf}
                                        onChange={(e) => setFormData({ ...formData, generate_pdf: e.target.checked })}
                                        className="rounded border-white/20 text-gold focus:ring-gold/50"
                                        data-testid="generate-pdf-checkbox"
                                    />
                                    <div>
                                        <p className="text-sm text-white font-medium">Download PDF Vouchers</p>
                                        <p className="text-xs text-muted-foreground">Get a branded PDF with QR codes, voucher codes, and redemption instructions</p>
                                    </div>
                                    <FileText className="w-5 h-5 text-gold ml-auto shrink-0" />
                                </label>
                                <div className="flex justify-end gap-3 pt-4">
                                    <Button type="button" variant="outline" onClick={() => setDialogOpen(false)} className="border-white/10">
                                        Cancel
                                    </Button>
                                    <Button type="submit" className="bg-gold text-[#0A0D14] hover:bg-gold/90" data-testid="generate-submit-btn">
                                        Generate Codes
                                    </Button>
                                </div>
                            </form>
                        </DialogContent>
                    </Dialog>
                    </div>
                </div>

                {/* Filter Tabs */}
                <Tabs value={filter} onValueChange={setFilter}>
                    <TabsList className="bg-[#121620] border border-white/10">
                        <TabsTrigger value="all" className="data-[state=active]:bg-gold data-[state=active]:text-[#0A0D14]">
                            All ({codes.length})
                        </TabsTrigger>
                        <TabsTrigger value="active" className="data-[state=active]:bg-gold data-[state=active]:text-[#0A0D14]">
                            Active ({codes.filter(c => c.is_active && c.current_uses < c.max_uses).length})
                        </TabsTrigger>
                        <TabsTrigger value="used" className="data-[state=active]:bg-gold data-[state=active]:text-[#0A0D14]">
                            Used ({codes.filter(c => c.current_uses >= c.max_uses).length})
                        </TabsTrigger>
                    </TabsList>
                </Tabs>

                {/* Codes Table */}
                <Card className="bg-[#121620] border-white/10">
                    <CardContent className="p-0">
                        <Table>
                            <TableHeader>
                                <TableRow className="border-white/10 hover:bg-transparent">
                                    <TableHead className="text-muted-foreground">Code</TableHead>
                                    <TableHead className="text-muted-foreground">Redeemed By</TableHead>
                                    <TableHead className="text-muted-foreground">Source</TableHead>
                                    <TableHead className="text-muted-foreground">Devices</TableHead>
                                    <TableHead className="text-muted-foreground">Expires</TableHead>
                                    <TableHead className="text-muted-foreground">Status</TableHead>
                                    <TableHead className="text-muted-foreground text-right">Actions</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {filteredCodes.map((code) => (
                                    <TableRow key={code.id} className="border-white/10 hover:bg-white/5">
                                        <TableCell>
                                            <div className="flex items-center gap-3">
                                                <button 
                                                    onClick={() => openQrDialog(code)}
                                                    className="w-8 h-8 rounded-md bg-gold/10 flex items-center justify-center hover:bg-gold/20 transition-colors"
                                                >
                                                    <QrCode className="w-4 h-4 text-gold" />
                                                </button>
                                                <div>
                                                    <code className="font-mono text-white">{code.code}</code>
                                                    {code.label && <p className="text-[10px] text-muted-foreground">{code.label}</p>}
                                                </div>
                                            </div>
                                        </TableCell>
                                        <TableCell>
                                            {code.redeemed_by && code.redeemed_by.length > 0 ? (
                                                <div className="space-y-0.5">
                                                    {code.redeemed_by.map((r, i) => (
                                                        <p key={i} className="text-xs text-emerald-400 truncate max-w-[180px]" title={r.email}>
                                                            {r.email}
                                                        </p>
                                                    ))}
                                                </div>
                                            ) : (
                                                <span className="text-xs text-muted-foreground">—</span>
                                            )}
                                        </TableCell>
                                        <TableCell>
                                            <span className={`px-2 py-0.5 rounded-full text-[10px] ${
                                                code.source === 'admin_bulk' ? 'bg-blue-500/10 text-blue-400' :
                                                code.source === 'admin_manual' ? 'bg-gold/10 text-gold' :
                                                'bg-white/5 text-muted-foreground'
                                            }`}>
                                                {code.source === 'admin_bulk' ? 'Admin Assigned' :
                                                 code.source === 'admin_manual' ? 'Admin' : 'Self-Generated'}
                                            </span>
                                        </TableCell>
                                        <TableCell className="text-muted-foreground">
                                            {code.current_uses}/{code.max_uses}
                                        </TableCell>
                                        <TableCell>
                                            <div className="flex items-center gap-1 text-muted-foreground">
                                                <Calendar className="w-4 h-4" />
                                                <span>{formatDate(code.expires_at)}</span>
                                            </div>
                                        </TableCell>
                                        <TableCell>
                                            <span className={`px-2 py-1 rounded-full text-xs ${
                                                code.current_uses >= code.max_uses ? 'bg-red-500/10 text-red-400' :
                                                code.is_active ? 'bg-emerald-500/10 text-emerald-400' : 
                                                'bg-gray-500/10 text-gray-400'
                                            }`}>
                                                {code.current_uses >= code.max_uses ? 'Used' : code.is_active ? 'Active' : 'Expired'}
                                            </span>
                                        </TableCell>
                                        <TableCell className="text-right">
                                            <div className="flex items-center justify-end gap-1">
                                                <Button 
                                                    variant="ghost" 
                                                    size="sm" 
                                                    onClick={() => copyCode(code.code)}
                                                    className="text-muted-foreground hover:text-white"
                                                    title="Copy code"
                                                >
                                                    {copiedCode === code.code ? <Check className="w-4 h-4 text-emerald-400" /> : <Copy className="w-4 h-4" />}
                                                </Button>
                                                <Button 
                                                    variant="ghost" 
                                                    size="sm" 
                                                    onClick={() => openSendDialog(code)}
                                                    className="text-muted-foreground hover:text-gold"
                                                    title="Send to traveler"
                                                    disabled={code.current_uses >= code.max_uses}
                                                >
                                                    <Send className="w-4 h-4" />
                                                </Button>
                                                <Button 
                                                    variant="ghost" 
                                                    size="sm" 
                                                    onClick={() => downloadPdf([code.id])}
                                                    className="text-muted-foreground hover:text-gold"
                                                    title="Download PDF voucher"
                                                    data-testid={`download-pdf-${code.id}`}
                                                >
                                                    <FileText className="w-4 h-4" />
                                                </Button>
                                            </div>
                                        </TableCell>
                                    </TableRow>
                                ))}
                                {filteredCodes.length === 0 && (
                                    <TableRow>
                                        <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">
                                            No codes found. Generate your first codes to get started.
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </CardContent>
                </Card>

                {/* Send Code Dialog */}
                <Dialog open={sendDialogOpen} onOpenChange={setSendDialogOpen}>
                    <DialogContent className="bg-[#121620] border-white/10 max-w-md">
                        <DialogHeader>
                            <DialogTitle className="text-white font-outfit">Send Code to Traveler</DialogTitle>
                        </DialogHeader>
                        <form onSubmit={handleSendCode} className="space-y-4 mt-4">
                            <div className="p-3 rounded-md bg-gold/10 border border-gold/30 text-center">
                                <p className="text-sm text-muted-foreground">Code</p>
                                <p className="text-xl font-mono font-bold text-gold">{selectedCode?.code}</p>
                            </div>
                            
                            <div className="space-y-2">
                                <Label>Traveler Name</Label>
                                <Input
                                    value={sendData.traveler_name}
                                    onChange={(e) => setSendData({ ...sendData, traveler_name: e.target.value })}
                                    placeholder="John Doe"
                                    required
                                    className="bg-[#0A0D14] border-white/10"
                                />
                            </div>
                            
                            <div className="space-y-2">
                                <Label>Send via</Label>
                                <div className="grid grid-cols-2 gap-3">
                                    <button
                                        type="button"
                                        onClick={() => setSendData({ ...sendData, method: 'email' })}
                                        className={`p-3 rounded-md border flex items-center justify-center gap-2 transition-colors ${
                                            sendData.method === 'email' 
                                                ? 'bg-gold/10 border-gold text-gold' 
                                                : 'bg-[#0A0D14] border-white/10 text-muted-foreground'
                                        }`}
                                    >
                                        <Mail className="w-4 h-4" />
                                        Email
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => setSendData({ ...sendData, method: 'sms' })}
                                        className={`p-3 rounded-md border flex items-center justify-center gap-2 transition-colors ${
                                            sendData.method === 'sms' 
                                                ? 'bg-gold/10 border-gold text-gold' 
                                                : 'bg-[#0A0D14] border-white/10 text-muted-foreground'
                                        }`}
                                    >
                                        <Phone className="w-4 h-4" />
                                        SMS
                                    </button>
                                </div>
                            </div>
                            
                            {sendData.method === 'email' ? (
                                <div className="space-y-2">
                                    <Label>Email Address</Label>
                                    <Input
                                        type="email"
                                        value={sendData.email}
                                        onChange={(e) => setSendData({ ...sendData, email: e.target.value })}
                                        placeholder="traveler@example.com"
                                        required
                                        className="bg-[#0A0D14] border-white/10"
                                    />
                                </div>
                            ) : (
                                <div className="space-y-2">
                                    <Label>Phone Number</Label>
                                    <Input
                                        type="tel"
                                        value={sendData.phone}
                                        onChange={(e) => setSendData({ ...sendData, phone: e.target.value })}
                                        placeholder="+1234567890"
                                        required
                                        className="bg-[#0A0D14] border-white/10"
                                    />
                                </div>
                            )}
                            
                            <div className="flex justify-end gap-3 pt-4">
                                <Button type="button" variant="outline" onClick={() => setSendDialogOpen(false)} className="border-white/10">
                                    Cancel
                                </Button>
                                <Button type="submit" className="bg-gold text-[#0A0D14] hover:bg-gold/90 gap-2" disabled={sending}>
                                    {sending ? (
                                        <>
                                            <Loader2 className="w-4 h-4 animate-spin" />
                                            Sending...
                                        </>
                                    ) : (
                                        <>
                                            <Send className="w-4 h-4" />
                                            Send Code
                                        </>
                                    )}
                                </Button>
                            </div>
                        </form>
                    </DialogContent>
                </Dialog>

                {/* QR Code Dialog */}
                <Dialog open={qrDialogOpen} onOpenChange={setQrDialogOpen}>
                    <DialogContent className="bg-[#121620] border-white/10 max-w-sm">
                        <DialogHeader>
                            <DialogTitle className="text-white font-outfit text-center">QR Code</DialogTitle>
                        </DialogHeader>
                        <div className="flex flex-col items-center py-4">
                            {selectedCode && (
                                <>
                                    <div className="bg-white p-4 rounded-md">
                                        <img 
                                            src={`${API}/api/qr/${selectedCode.code}`} 
                                            alt={`QR code for ${selectedCode.code}`}
                                            className="w-48 h-48"
                                        />
                                    </div>
                                    <p className="mt-4 font-mono text-lg text-gold">{selectedCode.code}</p>
                                    <Button 
                                        onClick={downloadQr}
                                        className="mt-4 bg-gold text-[#0A0D14] hover:bg-gold/90 gap-2"
                                    >
                                        <Download className="w-4 h-4" />
                                        Download QR
                                    </Button>
                                </>
                            )}
                        </div>
                    </DialogContent>
                </Dialog>
            </div>
        </DashboardLayout>
    );
}
